import { vmsRegisteredVendorsAPI } from '../services/api';
import type {
  OnboardingFormRow,
  OnboardingPipelineSummaryDto,
} from '../pages/vms/onboardingPipelineStatus';
import { unwrapDpiaRowsWithCode } from '../pages/vms/onboardingPipelineStatus';
import { parseVmsIsMaterialFromApi } from './vmsMateriality';

const CACHE_TTL_MS = 30_000;

type CacheEntry = { at: number; data: OnboardingPipelineSummaryDto };

const summaryCache = new Map<number, CacheEntry>();
const summaryInflight = new Map<number, Promise<OnboardingPipelineSummaryDto | null>>();

const dpiaRowsCache = new Map<number, { at: number; data: OnboardingFormRow[] }>();
const dpiaRowsInflight = new Map<number, Promise<OnboardingFormRow[]>>();

const regIdByInitiationCache = new Map<number, { at: number; regId: number | null }>();
const regIdByInitiationInflight = new Map<number, Promise<number | null>>();

function normalizePipelineSummary(raw: unknown): OnboardingPipelineSummaryDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const annexure = (o.annexure ?? 'pending') as OnboardingPipelineSummaryDto['annexure'];
  const dpia = (o.dpia ?? 'pending') as OnboardingPipelineSummaryDto['dpia'];
  const contract = (o.contract ?? 'pending') as OnboardingPipelineSummaryDto['contract'];
  const notification_regulator = (o.notification_regulator ?? 'pending') as OnboardingPipelineSummaryDto['notification_regulator'];
  const register_filling = (o.register_filling ?? 'pending') as OnboardingPipelineSummaryDto['register_filling'];
  const prerequisites_complete =
    o.prerequisites_complete === true ||
    (annexure === 'done' &&
      dpia === 'done' &&
      contract === 'done' &&
      notification_regulator === 'done');
  const all_complete =
    o.all_complete === true || (prerequisites_complete && register_filling === 'done');
  const dpia_applicable = o.dpia_applicable === true;
  const is_material =
    o.is_material !== undefined ? parseVmsIsMaterialFromApi(o.is_material) : undefined;
  return {
    annexure,
    dpia,
    contract,
    notification_regulator,
    register_filling,
    prerequisites_complete,
    all_complete,
    dpia_applicable,
    ...(is_material !== undefined ? { is_material } : {}),
  };
}

function parsePositiveInt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw) && raw > 0) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return parseInt(raw.trim(), 10);
  return null;
}

/** Cached + deduped pipeline status for journey strip (avoids pool exhaustion from duplicate mounts). */
export async function fetchOnboardingPipelineStatusCached(
  registeredVendorId: number,
): Promise<OnboardingPipelineSummaryDto | null> {
  const regId = registeredVendorId;
  if (!Number.isInteger(regId) || regId < 1) return null;

  const hit = summaryCache.get(regId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;

  const existing = summaryInflight.get(regId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const raw = await vmsRegisteredVendorsAPI.getOnboardingPipelineStatus(regId);
      const data = normalizePipelineSummary(raw);
      if (data) summaryCache.set(regId, { at: Date.now(), data });
      return data;
    } catch {
      return null;
    } finally {
      summaryInflight.delete(regId);
    }
  })();

  summaryInflight.set(regId, promise);
  return promise;
}

/** Cached + deduped DPIA rows for pipeline dropdown and stage UI. */
export async function fetchOnboardingDpiaRowsCached(
  registeredVendorId: number,
): Promise<OnboardingFormRow[]> {
  const regId = registeredVendorId;
  if (!Number.isInteger(regId) || regId < 1) return [];

  const hit = dpiaRowsCache.get(regId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;

  const existing = dpiaRowsInflight.get(regId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const raw = await vmsRegisteredVendorsAPI.getDpia(regId);
      const data = unwrapDpiaRowsWithCode(raw);
      dpiaRowsCache.set(regId, { at: Date.now(), data });
      return data;
    } catch {
      return [];
    } finally {
      dpiaRowsInflight.delete(regId);
    }
  })();

  dpiaRowsInflight.set(regId, promise);
  return promise;
}

/** Resolve a single registered vendor id for an initiation (cached + deduped). */
export async function resolveRegisteredVendorIdForInitiationCached(
  vendorInitiationId: number,
  preferredRegisteredVendorId?: number | null,
): Promise<number | null> {
  const preferred = parsePositiveInt(preferredRegisteredVendorId ?? null);
  if (preferred != null) return preferred;

  const initiationId = vendorInitiationId;
  if (!Number.isInteger(initiationId) || initiationId < 1) return null;

  const hit = regIdByInitiationCache.get(initiationId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.regId;

  const existing = regIdByInitiationInflight.get(initiationId);
  if (existing) return existing;

  const promise = (async () => {
    let regId: number | null = null;
    try {
      const res = await vmsRegisteredVendorsAPI.getOnboardingAdditional(initiationId);
      const recs = (res?.data?.records ?? res?.records ?? []) as { id?: unknown }[];
      if (recs.length === 1) {
        regId = parsePositiveInt(recs[0]?.id);
      }
    } catch {
      regId = null;
    }
    regIdByInitiationCache.set(initiationId, { at: Date.now(), regId });
    return regId;
  })();

  regIdByInitiationInflight.set(initiationId, promise);
  return promise;
}

export function invalidateOnboardingPipelineStatusCache(registeredVendorId?: number): void {
  if (registeredVendorId != null && registeredVendorId > 0) {
    summaryCache.delete(registeredVendorId);
    summaryInflight.delete(registeredVendorId);
    dpiaRowsCache.delete(registeredVendorId);
    dpiaRowsInflight.delete(registeredVendorId);
    return;
  }
  summaryCache.clear();
  summaryInflight.clear();
  dpiaRowsCache.clear();
  dpiaRowsInflight.clear();
  regIdByInitiationCache.clear();
  regIdByInitiationInflight.clear();
}
